import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const ColumnMappingPanel = ({ 
  sourceColumns = [], 
  mappings = {}, 
  onMappingChange,
  onAutoMap,
  onResetMappings 
}) => {
  const [expandedColumns, setExpandedColumns] = useState(new Set());

  const standardFields = [
    { value: '', label: 'Select field...', disabled: true },
    { value: 'product_name', label: 'Product Name', description: 'Main product identifier' },
    { value: 'brand', label: 'Brand', description: 'Product brand or manufacturer' },
    { value: 'mpn', label: 'MPN', description: 'Manufacturer Part Number' },
    { value: 'ean', label: 'EAN/UPC', description: 'Barcode identifier' },
    { value: 'sku', label: 'SKU', description: 'Stock Keeping Unit' },
    { value: 'price', label: 'Price', description: 'Product price' },
    { value: 'stock', label: 'Stock Quantity', description: 'Available inventory' },
    { value: 'category', label: 'Category', description: 'Product category' },
    { value: 'description', label: 'Description', description: 'Product description' },
    { value: 'supplier', label: 'Supplier', description: 'Supplier name' },
    { value: 'ignore', label: 'Ignore Column', description: 'Skip this column' }
  ];

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return 'text-success';
    if (confidence >= 0.7) return 'text-warning';
    return 'text-error';
  };

  const getConfidenceIcon = (confidence) => {
    if (confidence >= 0.9) return 'CheckCircle';
    if (confidence >= 0.7) return 'AlertCircle';
    return 'XCircle';
  };

  const toggleColumnExpansion = (columnIndex) => {
    const newExpanded = new Set(expandedColumns);
    if (newExpanded?.has(columnIndex)) {
      newExpanded?.delete(columnIndex);
    } else {
      newExpanded?.add(columnIndex);
    }
    setExpandedColumns(newExpanded);
  };

  const handleMappingChange = (columnName, fieldValue) => {
    onMappingChange(columnName, fieldValue);
  };

  const getMappedFieldsCount = () => {
    return Object.values(mappings)?.filter(mapping => mapping?.field && mapping?.field !== 'ignore')?.length;
  };

  const getRequiredFieldsStatus = () => {
    const requiredFields = ['product_name', 'price'];
    const mappedFields = Object.values(mappings)?.map(m => m?.field);
    return requiredFields?.map(field => ({
      field,
      mapped: mappedFields?.includes(field)
    }));
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Column Mapping</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Map source columns to standard fields ({getMappedFieldsCount()}/{sourceColumns?.length} mapped)
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onAutoMap}
              iconName="Zap"
              iconPosition="left"
            >
              Auto-Map All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onResetMappings}
              iconName="RotateCcw"
              iconPosition="left"
            >
              Reset
            </Button>
          </div>
        </div>

        {/* Required Fields Status */}
        <div className="mt-3 flex items-center space-x-4">
          <span className="text-xs font-medium text-muted-foreground">Required Fields:</span>
          {getRequiredFieldsStatus()?.map(({ field, mapped }) => (
            <div key={field} className="flex items-center space-x-1">
              <Icon 
                name={mapped ? 'CheckCircle' : 'AlertCircle'} 
                size={12} 
                className={mapped ? 'text-success' : 'text-error'}
              />
              <span className={`text-xs ${mapped ? 'text-success' : 'text-error'}`}>
                {field?.replace('_', ' ')}
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* Column Mappings */}
      <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
        {sourceColumns?.map((column, index) => {
          const mapping = mappings?.[column?.name] || {};
          const isExpanded = expandedColumns?.has(index);
          const hasMapping = mapping?.field && mapping?.field !== '';
          const isRequired = ['product_name', 'price']?.includes(mapping?.field);

          return (
            <div 
              key={`${column?.name}-${index}`}
              className={`
                border rounded-lg transition-all duration-200
                ${hasMapping ? 'border-success bg-success bg-opacity-5' : 'border-border bg-muted bg-opacity-50'}
                ${isRequired ? 'ring-1 ring-primary ring-opacity-20' : ''}
              `}
            >
              <div className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h4 className="text-sm font-medium text-foreground truncate">
                        {column?.name}
                      </h4>
                      {mapping?.confidence && (
                        <div className="flex items-center space-x-1">
                          <Icon 
                            name={getConfidenceIcon(mapping?.confidence)} 
                            size={12} 
                            className={getConfidenceColor(mapping?.confidence)}
                          />
                          <span className={`text-xs ${getConfidenceColor(mapping?.confidence)}`}>
                            {Math.round(mapping?.confidence * 100)}%
                          </span>
                        </div>
                      )}
                      {isRequired && (
                        <span className="px-1.5 py-0.5 bg-primary text-primary-foreground text-xs rounded">
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Type: {column?.type} • Sample: "{column?.sample}"
                    </p>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleColumnExpansion(index)}
                      iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                      className="h-6 w-6"
                    />
                  </div>
                </div>

                {/* Mapping Selector */}
                <div className="mt-3">
                  <Select
                    options={standardFields}
                    value={mapping?.field || ''}
                    onChange={(value) => handleMappingChange(column?.name, value)}
                    placeholder="Select field mapping..."
                    className="w-full"
                  />
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <span className="font-medium text-muted-foreground">Data Type:</span>
                        <p className="text-foreground mt-1">{column?.type}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Non-null Count:</span>
                        <p className="text-foreground mt-1">{column?.nonNullCount || 0}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Unique Values:</span>
                        <p className="text-foreground mt-1">{column?.uniqueCount || 0}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Sample Values:</span>
                        <p className="text-foreground mt-1 font-mono">
                          {column?.samples?.slice(0, 3)?.join(', ') || column?.sample}
                        </p>
                      </div>
                    </div>

                    {mapping?.field && mapping?.field !== 'ignore' && (
                      <div className="mt-3 p-2 bg-muted rounded">
                        <span className="text-xs font-medium text-muted-foreground">
                          Mapped to: {standardFields?.find(f => f?.value === mapping?.field)?.label}
                        </span>
                        {mapping?.confidence && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Auto-detected with {Math.round(mapping?.confidence * 100)}% confidence
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Footer Stats */}
      <div className="p-4 border-t border-border bg-muted bg-opacity-50">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center space-x-4">
            <span className="text-muted-foreground">
              <span className="font-medium text-foreground">{getMappedFieldsCount()}</span> of {sourceColumns?.length} columns mapped
            </span>
            <span className="text-muted-foreground">
              <span className="font-medium text-success">
                {getRequiredFieldsStatus()?.filter(f => f?.mapped)?.length}
              </span> of 2 required fields mapped
            </span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Info" size={12} className="text-muted-foreground" />
            <span className="text-muted-foreground">
              Product Name and Price are required
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColumnMappingPanel;